import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toaster } from '../components/ui/toaster';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import PricingComparisonTable from '../components/PricingComparisonTable';
import { tokens } from '../design-system';
import LinkAccountModal from '../components/LinkAccountModal';
import AuthFlow from '../components/AuthFlow';
import { OnboardingWizard } from '../components/onboarding';
import useMediaQuery from '../hooks/useMediaQuery';
import PublicNav from '../components/PublicNav';

// ============================================================================
// COTTAGECORE TOKENS — local alias for hardcoded light values
// ============================================================================

const T = {
  paper:      '#F7EFD8',
  paperDeep:  '#EEE2BF',
  paperSoft:  '#FDF7E5',
  card:       '#FFFBEC',
  ink:        '#2E2109',
  ink2:       '#4A3510',
  inkMuted:   '#6E5A38',
  inkFaint:   '#A39068',
  rule:       '#D7C9A1',
  ruleStrong: '#BFAB79',
  moss:       '#6B7A3E',
  mossDark:   '#4E5A29',
  mossSoft:   '#D7DCB9',
  capRed:     '#C14A33',
  capRedSoft: '#F2C7BA',
  roof:       '#C76A3B',
  stem:       '#E8C7A3',
  orange:     '#EA7C1F',
  orangeDark: '#C25F0E',
  leaf:       '#8FA14E',
  sh2:        '0 2px 0 rgba(46,33,9,0.04), 0 8px 16px -8px rgba(46,33,9,0.14)',
  sh3:        '0 3px 0 rgba(46,33,9,0.05), 0 20px 36px -12px rgba(46,33,9,0.18)',
  sans:       '"Nunito", ui-sans-serif, system-ui, -apple-system, sans-serif',
  hand:       '"Caveat", "Nunito", cursive',
  mono:       '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace',
};

// ============================================================================
// DECORATIVE ELEMENTS
// ============================================================================

const Mushroom = ({ size = 44, color = T.capRed, rot = 0, style = {} }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" style={{ transform: `rotate(${rot}deg)`, display: 'block', ...style }}>
    <path d="M6 22 C6 12, 16 5, 24 5 C32 5, 42 12, 42 22 C42 24, 40 25, 38 25 L10 25 C8 25, 6 24, 6 22 Z" fill={color}/>
    <ellipse cx="16" cy="14" rx="3" ry="2.5" fill="#FFFBEC" opacity="0.9"/>
    <ellipse cx="28" cy="11" rx="2.5" ry="2" fill="#FFFBEC" opacity="0.9"/>
    <ellipse cx="34" cy="18" rx="2" ry="1.8" fill="#FFFBEC" opacity="0.9"/>
    <path d="M16 25 L16 40 C16 42, 18 44, 20 44 L28 44 C30 44, 32 42, 32 40 L32 25 Z" fill={T.stem}/>
    <line x1="14" y1="26" x2="34" y2="26" stroke={T.ink} strokeWidth="0.8" opacity="0.3"/>
  </svg>
);

const Cottage = ({ size = 56, style = {} }) => (
  <svg width={size} height={size} viewBox="0 0 64 64" style={{ display: 'block', ...style }}>
    <path d="M6 30 L32 8 L58 30 L54 30 L54 32 L10 32 L10 30 Z" fill={T.roof}/>
    <rect x="12" y="32" width="40" height="22" fill={T.stem}/>
    <path d="M27 42 Q27 38 32 38 Q37 38 37 42 L37 54 L27 54 Z" fill={T.ink2}/>
    <circle cx="35" cy="48" r="0.8" fill={T.orange}/>
    <rect x="16" y="38" width="8" height="7" fill={T.orange} opacity="0.7"/>
    <line x1="20" y1="38" x2="20" y2="45" stroke={T.ink} strokeWidth="0.5"/>
    <line x1="16" y1="41.5" x2="24" y2="41.5" stroke={T.ink} strokeWidth="0.5"/>
    <rect x="44" y="14" width="5" height="10" fill={T.roof}/>
    <path d="M4 54 Q16 52, 32 54 T60 54 L60 58 L4 58 Z" fill={T.leaf} opacity="0.5"/>
  </svg>
);

const Leaf = ({ size = 20, color = T.leaf, rot = 0, style = {} }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" style={{ transform: `rotate(${rot}deg)`, display: 'block', ...style }}>
    <path d="M12 2 C6 6, 4 14, 12 22 C20 14, 18 6, 12 2 Z" fill={color}/>
    <path d="M12 4 L12 20" stroke={T.ink} strokeWidth="0.6" opacity="0.5"/>
  </svg>
);

const Stone = ({ size = 24, color = T.stem, style = {} }) => (
  <svg width={size} height={size} viewBox="0 0 32 24" style={{ display: 'block', ...style }}>
    <path d="M4 16 C4 10, 10 6, 16 6 C22 6, 28 10, 28 16 C28 20, 24 22, 16 22 C8 22, 4 20, 4 16 Z" fill={color}/>
    <path d="M8 14 Q12 10, 18 12" stroke={T.ink} strokeWidth="0.6" fill="none" opacity="0.3"/>
  </svg>
);

const Underline = ({ width = 120, color = T.orange, style = {} }) => (
  <svg width={width} height="10" viewBox={`0 0 ${width} 10`} style={{ display: 'block', ...style }}>
    <path
      d={`M2 6 Q${width * 0.25} 2, ${width * 0.5} 6 T${width - 3} 5`}
      stroke={color}
      strokeWidth="2.4"
      fill="none"
      strokeLinecap="round"
    />
  </svg>
);

const HandArrow = ({ length = 80, color = T.orange, style = {} }) => (
  <svg width={length} height="20" viewBox="0 0 80 20" style={{ display: 'block', ...style }}>
    <path d="M3 10 Q20 4, 40 10 T72 10" stroke={color} strokeWidth="1.6" fill="none" strokeLinecap="round"/>
    <path d="M66 5 L74 10 L66 15" stroke={color} strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const DashedRule = ({ style = {} }) => (
  <div style={{
    height: 1,
    backgroundImage: `repeating-linear-gradient(90deg, ${T.ruleStrong} 0 6px, transparent 6px 12px)`,
    ...style,
  }}/>
);

const CheckBullet = ({ color = T.moss }) => (
  <div style={{
    width: 22,
    height: 22,
    borderRadius: '50%',
    backgroundColor: color + '22',
    border: `1.5px solid ${color}40`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginTop: 2,
  }}>
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="3">
      <path d="M20 6L9 17l-5-5"/>
    </svg>
  </div>
);

// ============================================================================
// LANDING PAGE
// ============================================================================

const Landing = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, handleOAuthCallback } = useAuth();
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [accountConflictInfo, setAccountConflictInfo] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingUserInfo, setOnboardingUserInfo] = useState(null);

  useEffect(() => {
    if (location.state?.showLinkModal && location.state?.accountConflictInfo) {
      setAccountConflictInfo(location.state.accountConflictInfo);
      setShowLinkModal(true);
      window.history.replaceState({}, document.title);
      return;
    }

    const conflictInfoStr = sessionStorage.getItem('account_conflict_info');
    if (conflictInfoStr) {
      try {
        const conflictInfo = JSON.parse(conflictInfoStr);
        setAccountConflictInfo(conflictInfo);
        setShowLinkModal(true);
      } catch (e) {
        console.error('Failed to parse conflict info:', e);
        sessionStorage.removeItem('account_conflict_info');
      }
    }

    const checkOnboarding = sessionStorage.getItem('check_onboarding');
    if (checkOnboarding && user) {
      setOnboardingUserInfo({ email: user.email, provider: 'google', isNewUser: true });
      setShowOnboarding(true);
      sessionStorage.removeItem('check_onboarding');
    }
  }, [location.state, user]);

  useEffect(() => {
    if (user && !showOnboarding) {
      const isAdmin = user.role === 'admin';
      const hasPaidSubscription = ['enterprise', 'pro', 'hobbyist'].includes(user.subscription_tier) &&
        user.subscription_status === 'active';
      const hasCompletedOnboarding = user.onboarding_completed || localStorage.getItem('onboarding_completed');

      if (isAdmin || hasPaidSubscription || hasCompletedOnboarding) {
        navigate('/dashboard');
      } else {
        setOnboardingUserInfo({ email: user.email, provider: user.provider, isNewUser: false });
        setShowOnboarding(true);
      }
    }
  }, [user, navigate, showOnboarding]);

  const handleLinkSuccess = async (response) => {
    sessionStorage.removeItem('account_conflict_info');
    sessionStorage.removeItem('google_access_token');
    await handleOAuthCallback(response.user);
    toaster.success({
      title: 'Accounts Linked',
      description: 'Your Google account has been linked. You can now sign in with either method.',
    });
    navigate('/dashboard');
  };

  const handleLinkCancel = () => {
    setShowLinkModal(false);
    setAccountConflictInfo(null);
    sessionStorage.removeItem('account_conflict_info');
    sessionStorage.removeItem('google_access_token');
  };

  const handleStartOnboarding = (userInfo) => {
    setOnboardingUserInfo(userInfo);
    setShowOnboarding(true);
  };

  const handleOnboardingComplete = () => {
    localStorage.setItem('onboarding_completed', 'true');
    setShowOnboarding(false);
    navigate('/dashboard');
  };

  const scrollToLogin = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Scroll to hash section after navigation from another page
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash) return;
    const el = document.querySelector(hash);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    } else {
      // Element may not be rendered yet — wait one frame
      const id = requestAnimationFrame(() => {
        const el2 = document.querySelector(hash);
        if (el2) el2.scrollIntoView({ behavior: 'smooth' });
      });
      return () => cancelAnimationFrame(id);
    }
  }, []);

  return (
    <div style={{ minHeight: '100vh', backgroundColor: T.paper, fontFamily: T.sans, color: T.ink }}>
      <SEO
        title="AI planning that actually works"
        description="From idea to living documentation. Cahoots generates structured specs from your ideas or existing codebase and keeps them accurate as your product evolves."
      />

      <PublicNav onGetStarted={handleStartOnboarding} />

      {showOnboarding && (
        <OnboardingWizard
          userInfo={onboardingUserInfo}
          onComplete={handleOnboardingComplete}
        />
      )}

      {showLinkModal && accountConflictInfo && (
        <LinkAccountModal
          isOpen={showLinkModal}
          onClose={handleLinkCancel}
          onSuccess={handleLinkSuccess}
          accountInfo={accountConflictInfo}
        />
      )}

      <HeroSection onStartOnboarding={handleStartOnboarding} />
      <ProblemStatsSection />
      <DifferentiatorSection />
      <HowItWorksSection />
      <UseCasesSection />
      <PricingSection onGetStarted={scrollToLogin} />
      <FinalCTASection onGetStarted={scrollToLogin} />
      <Footer />
    </div>
  );
};

// ============================================================================
// HERO SECTION
// ============================================================================

const HeroSection = ({ onStartOnboarding }) => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 900px)');

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <section style={{
      backgroundColor: T.paper,
      position: 'relative',
      overflow: 'hidden',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Decorative corners */}
      {!isMobile && (
        <>
          <div style={{ position: 'absolute', top: 24, right: 80, opacity: 0.9, zIndex: 0 }}>
            <Mushroom size={58} rot={14}/>
          </div>
          <div style={{ position: 'absolute', bottom: 40, left: 48, opacity: 0.8, zIndex: 0 }}>
            <Cottage size={88}/>
          </div>
          <div style={{ position: 'absolute', bottom: 90, right: 60, display: 'flex', gap: 6, zIndex: 0 }}>
            <Leaf size={22} rot={-12}/>
            <Leaf size={18} rot={30} color={T.mossDark}/>
            <Stone size={24} style={{ alignSelf: 'flex-end' }}/>
          </div>
          <div style={{ position: 'absolute', top: 200, left: 32, display: 'flex', flexDirection: 'column', gap: 10, zIndex: 0 }}>
            <Leaf size={16} color={T.mossDark}/>
            <Leaf size={14} rot={45}/>
          </div>
        </>
      )}

      {/* Hero grid */}
      <div style={{
        padding: isMobile ? '32px 24px 0' : '48px 64px 0',
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1.15fr 0.85fr',
        gap: isMobile ? 40 : 56,
        alignItems: 'center',
        maxWidth: 1200,
        margin: '0 auto',
        position: 'relative',
        zIndex: 1,
        flex: 1,
        width: '100%',
        boxSizing: 'border-box',
        paddingBottom: isMobile ? 80 : 64,
      }}>
        {/* LEFT — copy */}
        <div style={{ order: isMobile ? 2 : 1 }}>
          {/* Kicker */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
            <Mushroom size={22} rot={-8}/>
            <span style={{
              fontFamily: T.hand,
              fontSize: 26,
              color: T.orange,
              transform: 'rotate(-1.5deg)',
              display: 'inline-block',
            }}>
              for product teams in cahoots
            </span>
          </div>

          {/* H1 */}
          <h1 style={{
            fontFamily: T.sans,
            fontSize: isMobile ? 42 : 72,
            fontWeight: 800,
            lineHeight: 1.03,
            letterSpacing: -1.4,
            color: T.ink,
            margin: '0 0 8px',
            maxWidth: 640,
          }}>
            Something gets lost{' '}
            <span style={{ position: 'relative', display: 'inline' }}>
              <span style={{ color: T.capRed }}>between idea</span>
              {!isMobile && (
                <Underline
                  width={260}
                  color={T.orange}
                  style={{ position: 'absolute', left: 0, bottom: -10, pointerEvents: 'none' }}
                />
              )}
            </span>{' '}
            and code.
          </h1>

          {/* Subcopy */}
          <p style={{
            fontSize: isMobile ? 17 : 20,
            color: T.ink2,
            lineHeight: 1.5,
            margin: '28px 0 28px',
            maxWidth: 560,
          }}>
            Cahoots helps teams catch missing requirements early, keep the plan up to date as things change, and spot drift before it turns into expensive rework.
          </p>

          {/* Bullets */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 32 }}>
            {[
              { text: 'Stop paying to do the same work twice', color: T.moss },
              { text: 'Find the gaps in your plan before you start building', color: T.orange },
              { text: 'See when the code and the plan start to drift apart', color: T.capRed },
              { text: 'Ship faster, with fewer expensive surprises', color: T.moss },
            ].map(({ text, color }, i) => (
              <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <CheckBullet color={color}/>
                <span style={{ fontSize: 17, color: T.ink2, lineHeight: 1.4 }}>{text}</span>
              </div>
            ))}
          </div>

        </div>

        {/* RIGHT — auth card */}
        <div style={{ order: isMobile ? 1 : 2, paddingTop: isMobile ? 0 : 8 }}>
          <div style={{
            backgroundColor: T.card,
            borderRadius: 14,
            border: `1px solid ${T.rule}`,
            boxShadow: T.sh3,
            transform: isMobile ? 'none' : 'rotate(1.2deg)',
            overflow: 'hidden',
          }}>
            {/* Moss header band */}
            <div style={{
              background: T.mossSoft,
              padding: '22px 28px 18px',
              borderBottom: `1px dashed ${T.ruleStrong}`,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontFamily: T.hand, fontSize: 26, color: T.mossDark, lineHeight: 1 }}>
                    come on in
                  </div>
                  <div style={{ fontSize: 14, color: T.ink2, marginTop: 6 }}>
                    Free to try · no credit card
                  </div>
                </div>
                <Mushroom size={36} rot={10}/>
              </div>
            </div>

            <div style={{ padding: '24px 28px 28px' }}>
              <AuthFlow
                onComplete={() => navigate('/dashboard')}
                onStartOnboarding={onStartOnboarding}
                lightTheme={true}
              />
            </div>
          </div>

          {/* Hand annotation */}
          {!isMobile && (
            <div style={{ position: 'relative', marginTop: 18, marginLeft: 60 }}>
              <HandArrow length={120} color={T.orange}/>
              <div style={{
                position: 'absolute',
                top: -20,
                left: 30,
                fontFamily: T.hand,
                fontSize: 22,
                color: T.orange,
                transform: 'rotate(-4deg)',
                pointerEvents: 'none',
              }}>
                about 30 seconds
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Scroll indicator */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 6,
        paddingBottom: 24,
        position: 'relative',
        zIndex: 1,
      }}>
        <ScrollChevron color={T.inkFaint}/>
      </div>
    </section>
  );
};

const ScrollChevron = ({ color = T.inkFaint }) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ animation: 'scrollBounce 1.6s ease-in-out infinite' }}>
    <path d="M4 7l6 6 6-6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <style>{`@keyframes scrollBounce { 0%, 100% { transform: translateY(0); opacity: 0.6; } 50% { transform: translateY(4px); opacity: 1; } }`}</style>
  </svg>
);

// ============================================================================
// PROBLEM / STATS SECTION
// ============================================================================

const ProblemStatsSection = () => {
  const isMobile = useMediaQuery('(max-width: 768px)');

  const stats = [
    {
      number: '31%',
      label: 'of software projects actually succeed',
      detail: "The leading cause of failure isn't bad code or missed deadlines. It's requirements that were never clear to begin with.",
      source: 'Standish CHAOS Report',
    },
    {
      number: '97%',
      label: 'more likely to succeed with clear requirements',
      detail: "The single biggest predictor of project success isn't the technology or the team. It's knowing exactly what you're building before you build it.",
      source: 'Engprax, 2024',
    },
    {
      number: '17 hrs',
      label: 'per week lost to maintenance and unclear decisions',
      detail: "Nearly half a developer's working week goes to rework caused by decisions nobody wrote down and requirements that shifted without warning.",
      source: 'Stripe Developer Coefficient',
    },
    {
      number: '$2.41T',
      label: 'lost annually to poor software quality',
      detail: 'Rework, failed projects, and features built to the wrong brief add up to a number that should make anyone shipping software pay attention.',
      source: 'CISQ, 2022',
    },
  ];

  return (
    <section style={{
      padding: '100px 24px',
      backgroundColor: T.paperSoft,
      borderTop: `1px dashed ${T.rule}`,
    }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 64 }}>
          <div style={{
            display: 'inline-block',
            padding: '6px 16px',
            backgroundColor: T.capRedSoft,
            borderRadius: 9999,
            marginBottom: 20,
          }}>
            <span style={{
              fontSize: 12,
              fontWeight: 700,
              color: T.capRed,
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
              fontFamily: T.mono,
            }}>
              The cost
            </span>
          </div>
          <h2 style={{
            fontSize: isMobile ? 30 : 40,
            fontWeight: 800,
            color: T.ink,
            marginBottom: 20,
            letterSpacing: -0.8,
            lineHeight: 1.2,
            fontFamily: T.sans,
          }}>
            The gap between intent and output<br />has a price tag
          </h2>
          <p style={{
            fontSize: 18,
            color: T.inkMuted,
            maxWidth: 600,
            margin: '0 auto',
            lineHeight: 1.6,
          }}>
            Unclear intent doesn't just slow projects down. It compounds into rework, missed deadlines, and features nobody needed.
          </p>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
          gap: 24,
          marginBottom: 64,
        }}>
          {stats.map((stat, i) => (
            <div key={i} style={{
              padding: 32,
              backgroundColor: T.card,
              borderRadius: 14,
              border: `1px solid ${T.rule}`,
              boxShadow: T.sh2,
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}>
              <div style={{
                fontSize: isMobile ? 40 : 52,
                fontWeight: 800,
                color: T.orange,
                letterSpacing: '-0.03em',
                lineHeight: 1,
                fontFamily: T.sans,
              }}>
                {stat.number}
              </div>
              <div style={{ fontSize: 17, fontWeight: 700, color: T.ink, lineHeight: 1.3 }}>
                {stat.label}
              </div>
              <p style={{ fontSize: 14, color: T.inkMuted, lineHeight: 1.6, margin: 0 }}>
                {stat.detail}
              </p>
              <div style={{
                fontSize: 12,
                color: T.inkFaint,
                fontFamily: T.mono,
                marginTop: 'auto',
                paddingTop: 8,
                borderTop: `1px dashed ${T.rule}`,
              }}>
                Source: {stat.source}
              </div>
            </div>
          ))}
        </div>

        <div style={{
          padding: isMobile ? '32px 24px' : '40px 48px',
          backgroundColor: T.paperDeep,
          borderRadius: 14,
          border: `1px dashed ${T.ruleStrong}`,
          textAlign: 'center',
        }}>
          <p style={{
            fontSize: isMobile ? 18 : 22,
            fontWeight: 700,
            color: T.ink,
            lineHeight: 1.5,
            margin: 0,
            fontFamily: T.sans,
          }}>
            Cahoots keeps the intent behind your project clear, current, and precise enough to build from. Less gets lost. Less gets rebuilt.
          </p>
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// DIFFERENTIATOR SECTION
// ============================================================================

const DifferentiatorSection = () => {
  const isMobile = useMediaQuery('(max-width: 768px)');

  const points = [
    {
      title: 'A plan that lives with your project',
      body: "Most plans are written once and immediately start going stale. Cahoots maintains a connected, structured plan that evolves with your project instead of gathering dust in a doc nobody reads.",
      color: T.moss,
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
          <polyline points="14 2 14 8 20 8"/>
          <line x1="16" y1="13" x2="8" y2="13"/>
          <line x1="16" y1="17" x2="8" y2="17"/>
        </svg>
      ),
    },
    {
      title: 'Change one thing, not everything',
      body: 'When scope shifts or a decision changes, everything connected updates automatically. No more hunting down every place the old decision lived and hoping you caught them all.',
      color: T.orange,
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="23 4 23 10 17 10"/>
          <polyline points="1 20 1 14 7 14"/>
          <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
        </svg>
      ),
    },
    {
      title: 'One source of truth, every angle covered',
      body: "Export your plan as a product brief, a technical handoff, an AI starter kit, or a Jira-ready breakdown. Every format generated from the same source so nothing gets out of sync.",
      color: T.roof,
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
          <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
        </svg>
      ),
    },
    {
      title: 'Plan vs. reality',
      badge: 'Pro',
      body: "Connect your GitHub repo and Cahoots tells you what's been built, what's partial, and what exists in code but never made it into the plan. No more assuming everything is still aligned.",
      color: T.capRed,
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
          <polyline points="17 8 12 3 7 8"/>
          <line x1="12" y1="3" x2="12" y2="15"/>
        </svg>
      ),
    },
  ];

  return (
    <section id="features" style={{ padding: '100px 24px', backgroundColor: T.paper }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 72 }}>
          <h2 style={{
            fontSize: 40,
            fontWeight: 800,
            color: T.ink,
            marginBottom: 16,
            letterSpacing: -0.8,
            fontFamily: T.sans,
          }}>
            Close the gap.
          </h2>
          <p style={{ fontSize: 18, color: T.inkMuted, maxWidth: 560, margin: '0 auto', lineHeight: 1.6 }}>
            Cahoots keeps the intent behind your project clear and current from the first idea to the last commit.
          </p>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
          gap: 24,
        }}>
          {points.map((point, i) => (
            <div key={i} style={{
              padding: 32,
              backgroundColor: T.card,
              borderRadius: 14,
              border: `1px solid ${T.rule}`,
              boxShadow: T.sh2,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                <div style={{
                  width: 44,
                  height: 44,
                  borderRadius: 10,
                  backgroundColor: point.color + '20',
                  color: point.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  {point.icon}
                </div>
                {point.badge && (
                  <span style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: T.orange,
                    backgroundColor: T.orange + '18',
                    border: `1px solid ${T.orange}40`,
                    borderRadius: 4,
                    padding: '2px 6px',
                    letterSpacing: '0.03em',
                    fontFamily: T.mono,
                  }}>
                    Pro
                  </span>
                )}
              </div>
              <h3 style={{
                fontSize: 20,
                fontWeight: 700,
                color: T.ink,
                marginBottom: 10,
                lineHeight: 1.3,
                fontFamily: T.sans,
              }}>
                {point.title}
              </h3>
              <p style={{ fontSize: 15, color: T.inkMuted, lineHeight: 1.65, margin: 0 }}>
                {point.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// HOW IT WORKS
// ============================================================================

const HowItWorksSection = () => {
  const isMobile = useMediaQuery('(max-width: 640px)');
  const isTablet = useMediaQuery('(max-width: 1024px)');

  const steps = [
    {
      number: '01',
      title: "Tell us what you're building",
      description: 'Describe a new idea or connect an existing repo. Cahoots asks the questions that surface what you actually need to build.',
      color: T.moss,
    },
    {
      number: '02',
      title: 'Get a plan worth building from',
      description: 'Cahoots generates a structured, connected plan your whole team can work from. Clear enough to hand off. Detailed enough to trust.',
      color: T.orange,
    },
    {
      number: '03',
      title: 'Keep it true as things change',
      description: 'Change one thing and everything connected updates. Connect your repo and see exactly where the code and the plan have parted ways.',
      color: T.capRed,
    },
  ];

  return (
    <section id="how-it-works" style={{ padding: '100px 24px', backgroundColor: T.paperDeep }}>
      <div style={{ maxWidth: 1000, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 64 }}>
          <h2 style={{
            fontSize: 40,
            fontWeight: 800,
            color: T.ink,
            marginBottom: 16,
            letterSpacing: -0.8,
            fontFamily: T.sans,
          }}>
            How it works
          </h2>
          <p style={{ fontSize: 18, color: T.inkMuted }}>
            Three steps from scattered intent to a plan that stays current.
          </p>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
          gap: isMobile ? 32 : 32,
          position: 'relative',
        }}>
          {steps.map((step, i) => (
            <div key={i} style={{ textAlign: 'center', position: 'relative' }}>
              <div style={{
                width: 72,
                height: 72,
                margin: '0 auto 24px',
                borderRadius: '50%',
                backgroundColor: step.color + '18',
                border: `2px solid ${step.color}40`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 22,
                fontWeight: 800,
                color: step.color,
                letterSpacing: -0.5,
                fontFamily: T.mono,
              }}>
                {step.number}
              </div>
              <h3 style={{
                fontSize: 18,
                fontWeight: 700,
                color: T.ink,
                marginBottom: 8,
                fontFamily: T.sans,
              }}>
                {step.title}
              </h3>
              <p style={{ fontSize: 14, color: T.inkMuted, lineHeight: 1.6 }}>
                {step.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// USE CASES
// ============================================================================

const UseCasesSection = () => {
  const isMobile = useMediaQuery('(max-width: 640px)');

  const personas = [
    {
      title: 'Solo developers',
      description: 'Move fast without losing track of what you decided.',
      color: T.moss,
      checks: [
        'Catch the missing pieces before the build starts',
        'Hand off to AI tools with context they can actually use',
        'Keep your plan current without a second job maintaining it',
        'Ship what you meant to build, not a rough approximation',
      ],
    },
    {
      title: 'Small teams',
      description: 'Stay aligned when everything is changing at once.',
      color: T.orange,
      checks: [
        'One plan everyone is working from',
        'Change one thing and watch the rest update automatically',
        'No more assuming the plan and the product still match',
        'Get new teammates up to speed fast',
      ],
    },
    {
      title: 'Product managers',
      description: "Get your intent into engineering without losing anything in translation.",
      color: T.capRed,
      checks: [
        'Turn ideas into plans engineering can build from',
        'Keep requirements clear as scope shifts',
        'Sync directly to Jira and Trello',
        'Always know where the plan and the code have diverged',
      ],
    },
  ];

  return (
    <section id="use-cases" style={{ padding: '100px 24px', backgroundColor: T.paper }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 64 }}>
          <h2 style={{
            fontSize: 40,
            fontWeight: 800,
            color: T.ink,
            marginBottom: 16,
            letterSpacing: -0.8,
            fontFamily: T.sans,
          }}>
            Built for anyone shipping software
          </h2>
          <p style={{ fontSize: 18, color: T.inkMuted, maxWidth: 520, margin: '0 auto', lineHeight: 1.6 }}>
            The gap between intent and output shows up differently depending on where you sit.
          </p>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
          gap: isMobile ? 16 : 24,
        }}>
          {personas.map((persona, i) => (
            <div key={i} style={{
              padding: isMobile ? 24 : 32,
              backgroundColor: T.card,
              borderRadius: 14,
              border: `1px solid ${T.rule}`,
              boxShadow: T.sh2,
              display: 'flex',
              flexDirection: 'column',
              gap: 20,
            }}>
              <div>
                <h3 style={{
                  fontSize: 20,
                  fontWeight: 700,
                  color: T.ink,
                  marginBottom: 6,
                  fontFamily: T.sans,
                }}>
                  {persona.title}
                </h3>
                <p style={{ fontSize: 14, color: T.inkMuted, margin: 0, lineHeight: 1.5 }}>
                  {persona.description}
                </p>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingTop: 4 }}>
                {persona.checks.map((check, j) => (
                  <div key={j} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <CheckBullet color={persona.color}/>
                    <span style={{ fontSize: 14, color: T.ink, lineHeight: 1.5 }}>
                      {check}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// PRICING SECTION
// ============================================================================

const PricingSection = ({ onGetStarted }) => {
  const isMobile = useMediaQuery('(max-width: 640px)');
  const isTablet = useMediaQuery('(max-width: 1024px)');

  const tiers = [
    {
      name: 'Free',
      price: '$0',
      period: '/month',
      description: 'Everything you need to get started',
      features: [
        'Idea discovery and specs',
        'Event modeling',
        'Features and stories',
        'Unlimited ideas and plans',
      ],
      cta: 'Try Cahoots',
      ctaAction: onGetStarted,
      highlighted: false,
    },
    {
      name: 'Hobbyist',
      price: '$10',
      period: '/month',
      description: 'For side projects and personal use',
      features: [
        'Everything in Free, plus:',
        'AI Chat',
        'Download gallery',
        'AI Starter Bundle',
      ],
      cta: 'Try Cahoots',
      ctaAction: onGetStarted,
      highlighted: false,
    },
    {
      name: 'Pro',
      price: '$50',
      period: '/month',
      description: 'For professional developers and teams',
      features: [
        'Everything in Hobbyist, plus:',
        'GitHub integration and drift detection',
        'Jira and Trello sync',
      ],
      cta: 'Try Cahoots',
      ctaAction: onGetStarted,
      highlighted: true,
      badge: 'Most Popular',
    },
    {
      name: 'Enterprise',
      price: 'Custom',
      period: '',
      description: 'For large teams with custom needs',
      features: [
        'Everything in Pro, plus:',
        'SSO/SAML authentication',
        'Custom integrations',
        'Priority support',
        'SLA guarantee',
      ],
      cta: 'Contact Sales',
      ctaAction: () => { window.location.href = 'mailto:sales@cahoots.cc'; },
      highlighted: false,
    },
  ];

  return (
    <section id="pricing" style={{ padding: '100px 24px', backgroundColor: T.paperSoft }}>
      <div style={{ maxWidth: 1400, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 64 }}>
          <h2 style={{
            fontSize: 40,
            fontWeight: 800,
            color: T.ink,
            marginBottom: 16,
            letterSpacing: -0.8,
            fontFamily: T.sans,
          }}>
            Simple pricing
          </h2>
          <p style={{ fontSize: 18, color: T.inkMuted, maxWidth: 600, margin: '0 auto' }}>
            Start free and upgrade as you grow. No hidden fees.
          </p>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
          gap: isMobile ? 16 : 20,
          alignItems: 'stretch',
        }}>
          {tiers.map((tier, i) => (
            <div key={i} style={{
              padding: isMobile ? 24 : 32,
              backgroundColor: tier.highlighted ? T.orange + '10' : T.card,
              borderRadius: 14,
              border: tier.highlighted
                ? `2px solid ${T.orange}`
                : `1px solid ${T.rule}`,
              boxShadow: tier.highlighted ? T.sh3 : T.sh2,
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
            }}>
              {tier.badge && (
                <div style={{
                  position: 'absolute',
                  top: -12,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  backgroundColor: T.orange,
                  color: '#fff',
                  padding: '4px 16px',
                  borderRadius: 9999,
                  fontSize: 12,
                  fontWeight: 700,
                  fontFamily: T.mono,
                  whiteSpace: 'nowrap',
                }}>
                  {tier.badge}
                </div>
              )}

              <div style={{ marginBottom: 24 }}>
                <h3 style={{
                  fontSize: 22,
                  fontWeight: 800,
                  color: T.ink,
                  marginBottom: 6,
                  fontFamily: T.sans,
                }}>
                  {tier.name}
                </h3>
                <p style={{ fontSize: 14, color: T.inkMuted, marginBottom: 16 }}>
                  {tier.description}
                </p>
                <div style={{ display: 'flex', alignItems: 'baseline' }}>
                  <span style={{
                    fontSize: 44,
                    fontWeight: 800,
                    color: T.ink,
                    fontFamily: T.sans,
                    letterSpacing: -1,
                  }}>
                    {tier.price}
                  </span>
                  {tier.period && (
                    <span style={{ fontSize: 15, color: T.inkMuted, marginLeft: 4 }}>
                      {tier.period}
                    </span>
                  )}
                </div>
              </div>

              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: '0 0 32px',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
              }}>
                {tier.features.map((feature, j) => (
                  <li key={j} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                    fontSize: 14,
                    color: T.ink,
                  }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.moss} strokeWidth="3" style={{ flexShrink: 0, marginTop: 1 }}>
                      <path d="M20 6L9 17l-5-5"/>
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>

              <button
                onClick={tier.ctaAction}
                style={{
                  width: '100%',
                  padding: '12px 20px',
                  fontSize: 14,
                  fontWeight: 700,
                  borderRadius: 10,
                  cursor: 'pointer',
                  border: 'none',
                  fontFamily: T.sans,
                  ...(tier.highlighted
                    ? { backgroundColor: T.orange, color: '#fff' }
                    : { backgroundColor: T.paperDeep, color: T.ink, border: `1px solid ${T.ruleStrong}` }),
                }}
              >
                {tier.cta}
              </button>
            </div>
          ))}
        </div>

        <div style={{
          marginTop: 64,
          backgroundColor: T.card,
          borderRadius: 14,
          border: `1px solid ${T.rule}`,
          overflow: 'hidden',
          boxShadow: T.sh2,
        }}>
          <PricingComparisonTable />
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// FINAL CTA
// ============================================================================

const FinalCTASection = ({ onGetStarted }) => {
  return (
    <section style={{
      padding: '120px 24px',
      backgroundColor: T.paperDeep,
      position: 'relative',
      overflow: 'hidden',
    }}>
      <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 800,
        height: 800,
        background: `radial-gradient(circle, ${T.orange}0D 0%, transparent 60%)`,
        pointerEvents: 'none',
      }}/>

      <div style={{
        maxWidth: 700,
        margin: '0 auto',
        textAlign: 'center',
        position: 'relative',
        zIndex: 1,
      }}>
        <div style={{
          fontFamily: T.hand,
          fontSize: 36,
          color: T.orange,
          marginBottom: 8,
          transform: 'rotate(-1deg)',
          display: 'inline-block',
        }}>
          ready to stop losing things?
        </div>
        <h2 style={{
          fontSize: 48,
          fontWeight: 800,
          color: T.ink,
          marginBottom: 24,
          lineHeight: 1.1,
          letterSpacing: -1,
          fontFamily: T.sans,
        }}>
          Nothing has to get lost.
        </h2>
        <p style={{
          fontSize: 20,
          color: T.inkMuted,
          marginBottom: 40,
          lineHeight: 1.6,
        }}>
          Turn your next idea into a plan that stays true. Free to try, no credit card required.
        </p>
        <button
          onClick={onGetStarted}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 10,
            padding: '18px 44px',
            fontSize: 18,
            fontWeight: 700,
            backgroundColor: T.orange,
            color: '#fff',
            border: 'none',
            borderRadius: 14,
            cursor: 'pointer',
            fontFamily: T.sans,
            boxShadow: T.sh3,
          }}
        >
          Try Cahoots
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M5 12h14M12 5l7 7-7 7"/>
          </svg>
        </button>
        <div style={{ marginTop: 16, fontSize: 13, color: T.inkFaint }}>
          Free forever · no credit card required
        </div>
      </div>
    </section>
  );
};

export default Landing;
